import { MantineThemeComponents } from "@mantine/core";
import { fonts } from "./fonts";

// ============================================
// Eventsmagic Component Theme — "Sunset Soirée"
// Cohesive radii, warm borders, coral focus rings, and a distinctive
// active-nav treatment. Kept light so component variants and autoContrast
// keep working.
//
// The brand's heading face (festive = Fraunces serif, business = Hanken grotesque) is `headings.fontFamily` in index.ts;
// Titles / Modal / Drawer headers read it through Mantine's variable.
//
// ⚠⚠ NO FONT IS WRITTEN HERE AS A VALUE — only as Mantine's variables (owner, 2026-10-25, on an event themed "Gender
// Reveal" whose drawer headings were still the app's Fraunces: "We should not use any styling from the app - all should
// come from a theme"). A component style is an INLINE style, so a literal font here beat every event theme's variables:
// every Title, Modal / Drawer title and Button on the public site drew the APP's fonts. `--mantine-font-family` /
// `--mantine-font-family-headings` are set from this same theme on :root (the dashboard looks exactly as before) and
// re-pointed by an event theme on its page and its drawers (@em/blocks `themeCssVars`).
// ============================================

// ---------------------------------------------------------------------------
// Shared input styling
// ---------------------------------------------------------------------------
// Labels follow the input's `size` (via the --input-label-size CSS variable
// Mantine sets from the size prop) instead of a hardcoded value — so a
// `size="md"` field gets a md label and every field in a row lines up.
// The `md` fallback keeps labels comfortably readable (house rule: no small
// text for primary labels).
//
// ⚠ The colours are VARIABLES with the app's greys as the fallback (2026-10-03): an EVENT theme sets
// `--em-input-label-color` / `--em-input-description-color` to its own ink (@em/blocks `themeCssVars`), or every label
// on a dark event page painted dark grey on a dark page. Nothing in the host app sets them, so it is unchanged.
const inputLabel = {
  fontWeight: 600,
  fontSize: "var(--input-label-size, var(--mantine-font-size-md))",
  lineHeight: 1.4,
  marginBottom: "0.375rem",
  color: "var(--em-input-label-color, var(--mantine-color-gray-8))",
};

// Descriptions (help text) sit BELOW the input, so a field with a hint no
// longer pushes its input down and breaks alignment with its row-mates.
const inputDescription = {
  fontSize: "var(--mantine-font-size-xs)",
  lineHeight: 1.4,
  marginTop: "0.25rem",
  marginBottom: 0,
  color: "var(--em-input-description-color, var(--mantine-color-gray-6))",
};

// Defaults every Input.Wrapper-based field shares: medium size, rounded,
// and description rendered under the control.
const inputDefaults = {
  radius: "md" as const,
  size: "md" as const,
  inputWrapperOrder: ["label", "input", "description", "error"] as (
    | "label"
    | "input"
    | "description"
    | "error"
  )[],
};

const inputStyles = { label: inputLabel, description: inputDescription };

export const getComponents = (): MantineThemeComponents => ({
  Button: {
    defaultProps: {
      radius: "md",
      // House rule: buttons read at a comfortable MEDIUM size by default (Mantine's
      // own default is "sm"). Explicit size="sm"/"xs" only for genuinely compact spots.
      size: "md",
    },
    styles: {
      root: {
        fontFamily: "var(--mantine-font-family)",
        fontWeight: 600,
        letterSpacing: "0.01em",
      },
    },
  },

  ActionIcon: {
    defaultProps: {
      radius: "md",
    },
  },

  Card: {
    defaultProps: {
      radius: "lg",
      shadow: "sm",
      withBorder: true,
    },
    styles: {
      root: {
        borderColor: "var(--mantine-color-gray-2)",
        backgroundColor: "var(--mantine-color-white)",
      },
    },
  },

  Paper: {
    defaultProps: {
      radius: "lg",
    },
  },

  // Every field shares the same label/description treatment + md sizing so
  // rows line up and labels read at a consistent, comfortable size.
  TextInput: { defaultProps: inputDefaults, styles: inputStyles },
  Textarea: { defaultProps: inputDefaults, styles: inputStyles },
  Select: { defaultProps: inputDefaults, styles: inputStyles },
  MultiSelect: { defaultProps: inputDefaults, styles: inputStyles },
  Autocomplete: { defaultProps: inputDefaults, styles: inputStyles },
  PasswordInput: { defaultProps: inputDefaults, styles: inputStyles },
  NumberInput: { defaultProps: inputDefaults, styles: inputStyles },
  ColorInput: { defaultProps: inputDefaults, styles: inputStyles },
  DateInput: { defaultProps: inputDefaults, styles: inputStyles },
  DatePickerInput: { defaultProps: inputDefaults, styles: inputStyles },
  DateTimePicker: { defaultProps: inputDefaults, styles: inputStyles },
  TimeInput: { defaultProps: inputDefaults, styles: inputStyles },
  MonthPickerInput: { defaultProps: inputDefaults, styles: inputStyles },
  YearPickerInput: { defaultProps: inputDefaults, styles: inputStyles },
  // Less-common text-entry controls Mantine otherwise leaves at its "sm" (14px)
  // default — bump them to md too so no field ever renders small enough to make
  // mobile browsers zoom on focus (see the mobile input-zoom guard in global.css).
  TagsInput: { defaultProps: inputDefaults, styles: inputStyles },
  PillsInput: { defaultProps: inputDefaults, styles: inputStyles },
  PinInput: { defaultProps: { size: "md" as const } },
  NativeSelect: { defaultProps: inputDefaults, styles: inputStyles },
  JsonInput: { defaultProps: inputDefaults, styles: inputStyles },
  // Standalone Input.Wrapper (e.g. wrapping a SegmentedControl) gets the SAME
  // themed label/description as real inputs, so labels look identical everywhere.
  // Custom controls (IconSelector, a SegmentedControl in a wrapper…) render their
  // own Input.Wrapper — give it the SAME description-below order as the real
  // inputs, or its hint sits between label and control and the row stops aligning.
  InputWrapper: { defaultProps: { inputWrapperOrder: inputDefaults.inputWrapperOrder }, styles: inputStyles },

  // Dropdown options (Select / MultiSelect / Autocomplete / TagsInput all render
  // through Combobox). Force a comfortable MEDIUM option size with generous
  // vertical padding so options are easy to read and tap on mobile — the default
  // renders them small and cramped together.
  Combobox: {
    styles: {
      option: {
        fontSize: "var(--mantine-font-size-md)",
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
      },
    },
  },

  // Toggles / choices: label reads as normal body text (md), not fine print. The colour is the input labels' variable
  // (an event theme sets it to its ink — a fixed gray-8 was near-black on a dark event page, 2026-10-25).
  Switch: {
    defaultProps: { size: "md" },
    styles: {
      label: {
        fontSize: "var(--mantine-font-size-md)",
        color: "var(--em-input-label-color, var(--mantine-color-gray-8))",
        lineHeight: 1.4,
      },
      description: inputDescription,
    },
  },
  Checkbox: {
    defaultProps: { size: "md" },
    styles: {
      label: { fontSize: "var(--mantine-font-size-md)", color: "var(--em-input-label-color, var(--mantine-color-gray-8))" },
      description: inputDescription,
    },
  },
  Radio: {
    defaultProps: { size: "md" },
    styles: {
      label: { fontSize: "var(--mantine-font-size-md)", color: "var(--em-input-label-color, var(--mantine-color-gray-8))" },
      description: inputDescription,
    },
  },
  SegmentedControl: {
    defaultProps: { size: "md", radius: "md" },
  },

  Modal: {
    defaultProps: {
      radius: "lg",
      centered: true,
      overlayProps: { backgroundOpacity: 0.5, blur: 4 },
    },
    styles: {
      title: {
        fontFamily: "var(--mantine-font-family-headings)",
        fontWeight: 600,
        fontSize: fonts.sizes.xl,
      },
    },
  },

  Drawer: {
    styles: {
      title: {
        fontFamily: "var(--mantine-font-family-headings)",
        fontWeight: 600,
        fontSize: fonts.sizes.xl,
      },
    },
  },

  Badge: {
    defaultProps: {
      radius: "sm",
    },
    styles: {
      root: {
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
    },
  },

  Tooltip: {
    defaultProps: {
      radius: "sm",
      color: "dark",
    },
  },

  NavLink: {
    styles: {
      root: {
        borderRadius: "0.625rem",
        fontWeight: 500,
      },
    },
  },

  Notification: {
    defaultProps: {
      radius: "md",
    },
    styles: {
      root: {
        boxShadow:
          "0 16px 36px -10px rgb(34 27 38 / 0.16), 0 6px 12px -6px rgb(34 27 38 / 0.10)",
      },
      title: {
        fontWeight: 600,
        fontSize: fonts.sizes.md,
      },
      description: {
        fontSize: fonts.sizes.sm,
      },
    },
  },

  Anchor: {
    styles: {
      root: {
        fontWeight: 500,
      },
    },
  },

  Title: {
    styles: {
      root: {
        fontFamily: "var(--mantine-font-family-headings)",
        letterSpacing: "-0.01em",
      },
    },
  },
});
